import React from "react";
import { Link, useOutletContext } from "react-router-dom";

const LAST_UPDATED = "November 4, 2026";

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-white mb-3">{title}</h2>
      <div className="text-gray-400 text-sm leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function EnSections() {
  return (
    <>
      <Section title="1. Who we are">
        <p>
          ShadowVault is a product of AfterLight Technologies Inc. ("AfterLight", "we", "us"). This
          policy explains what we collect, how we use it, and who can access it.
        </p>
      </Section>

      <Section title="2. Information we collect">
        <p>
          <strong className="text-gray-300">Account information:</strong> your email address and
          phone number (used to send login verification codes and account notices), and a hashed
          — never plaintext — copy of your password.
        </p>
        <p>
          <strong className="text-gray-300">Your files:</strong> the documents you upload, and the
          category/folder names you give them. Files are encrypted before being stored (see
          Section 3).
        </p>
        <p>
          <strong className="text-gray-300">Legacy Contact information:</strong> the name, email,
          phone, and access level of anyone you designate as a Legacy Contact.
        </p>
        <p>
          <strong className="text-gray-300">Access logs:</strong> a record of actions like uploads,
          views, downloads, and deletions, including a timestamp and the acting account — kept for
          security and support purposes.
        </p>
      </Section>

      <Section title="3. How your files are actually protected">
        <p>
          Each file is encrypted with AES-256-GCM using its own random key before it's stored. This
          is <strong className="text-gray-300">not a zero-knowledge system</strong>: AfterLight can
          decrypt your own files on your behalf (for example, when you click "View"), and will do
          the same for a Legacy Contact once access has been released to them. We do not have a
          standing practice of accessing your files outside of those actions you or your Legacy
          Contacts take.
        </p>
        <p>
          Each file's cryptographic hash — not the file, and not its contents — is separately
          submitted to OpenTimestamps and anchored to the Bitcoin blockchain, so its existence and
          integrity at a point in time can be independently verified later.
        </p>
      </Section>

      <Section title="4. Who can access your information">
        <p>
          You; any Legacy Contact you've designated, once access has been released to them under
          your Continuity Check-In settings; and AfterLight personnel, on a limited basis, to operate
          the Service, respond to support requests, or comply with a legal obligation.
        </p>
      </Section>

      <Section title="5. Third parties we rely on">
        <p>
          We use third-party providers to operate the Service: a hosting/database provider, Resend
          (email delivery), Twilio (SMS delivery), Stripe (payment processing), and the public
          OpenTimestamps calendar servers
          (receive only a file's hash, never its content). Each handles only what's needed to
          provide its specific function.
        </p>
      </Section>

      <Section title="6. Data retention and deletion">
        <p>
          Deleting a file is permanent: its encryption key is destroyed immediately and cannot be
          recovered by you or by us. There is no trash, backup, or undo. Deleting your account
          removes your account information; you should delete any files you want gone first.
        </p>
      </Section>

      <Section title="7. Your choices">
        <p>
          You can view, update, or delete your files and account information at any time from
          within the app. If you need help with a request you can't complete yourself, contact us
          through the 24/7 support chat or the Contact Us page.
        </p>
      </Section>

      <Section title="8. Security">
        <p>
          We use encryption, access controls, and two-factor login verification to help protect
          your account and documents. No system is perfectly secure, and we can't guarantee
          absolute security.
        </p>
      </Section>

      <Section title="9. Changes to this policy">
        <p>
          We may update this policy from time to time. We'll change the "Last updated" date above
          when we do.
        </p>
      </Section>

      <Section title="10. Contact">
        <p>
          Questions about this policy can be sent through the 24/7 support chat in the app, or by
          email to support@shadowvault.ca.
        </p>
      </Section>

    </>
  );
}

function ZhSections() {
  return (
    <>
      <Section title="1. 我们是谁">
        <p>
          ShadowVault 是 AfterLight Technologies Inc.（下称"AfterLight"或"我们"）旗下的产品。本政策说明我们收集哪些信息、如何使用这些信息，以及谁可以访问这些信息。
        </p>
      </Section>

      <Section title="2. 我们收集的信息">
        <p>
          <strong className="text-gray-300">账户信息：</strong>您的邮箱地址和手机号码（用于发送登录验证码和账户通知），以及您密码的哈希值——我们从不保存明文密码。
        </p>
        <p>
          <strong className="text-gray-300">您的文件：</strong>您上传的文件，以及您为其设置的分类/文件夹名称。文件在存储前均经过加密（见第 3 条）。
        </p>
        <p>
          <strong className="text-gray-300">传承联系人信息：</strong>您指定的每位传承联系人的姓名、邮箱、电话及访问权限。
        </p>
        <p>
          <strong className="text-gray-300">访问日志：</strong>上传、查看、下载、删除等操作记录，包括时间及操作账户——用于安全和客户支持。
        </p>
      </Section>

      <Section title="3. 您的文件如何受到保护">
        <p>
          每个文件在存储前都会使用其独立的随机密钥，以 AES-256-GCM 加密。本系统<strong className="text-gray-300">并非零知识系统</strong>：AfterLight 可以代您解密您自己的文件（例如您点击"查看"时），并在访问权限释放后为您的传承联系人解密。除您或您的传承联系人主动发起的操作外，我们不会常规访问您的文件。
        </p>
        <p>
          每个文件的加密哈希值（而非文件本身或其内容）会单独提交至 OpenTimestamps 并锚定到比特币区块链，以便日后独立验证该文件在某一时间点的存在性和完整性。
        </p>
      </Section>

      <Section title="4. 谁可以访问您的信息">
        <p>
          您本人；在您的定期确认设置触发访问释放后，您指定的传承联系人；以及 AfterLight 工作人员——仅在运营服务、处理支持请求或履行法律义务所需的有限范围内。
        </p>
      </Section>

      <Section title="5. 我们依赖的第三方">
        <p>
          我们使用第三方服务商来运营本服务：托管/数据库服务商、Resend（邮件发送）、Twilio（短信发送）、Stripe（支付处理），以及公共 OpenTimestamps 日历服务器（仅接收文件哈希值，从不接收文件内容）。每家服务商仅处理其特定功能所需的信息。
        </p>
      </Section>

      <Section title="6. 数据保留与删除">
        <p>
          删除文件是永久性的：其加密密钥会被立即销毁，您和我们都无法恢复。没有回收站、备份或撤销功能。删除账户会移除您的账户信息；如需删除文件，请先自行删除。
        </p>
      </Section>

      <Section title="7. 您的选择">
        <p>
          您可以随时在应用内查看、更新或删除您的文件和账户信息。如有无法自行完成的请求，请通过全天候支持聊天或"联系我们"页面与我们联系。
        </p>
      </Section>

      <Section title="8. 安全">
        <p>
          我们采用加密、访问控制和双重登录验证来帮助保护您的账户和文件。没有任何系统是绝对安全的，我们无法保证绝对的安全。
        </p>
      </Section>

      <Section title="9. 本政策的变更">
        <p>
          我们可能会不时更新本政策。更新时，我们会修改上方的"最后更新"日期。
        </p>
      </Section>

      <Section title="10. 联系我们">
        <p>
          如对本政策有任何疑问，请通过应用内的全天候支持聊天，或发送邮件至 support@shadowvault.ca 与我们联系。
        </p>
      </Section>
    </>
  );
}

export default function PrivacyPolicy() {
  const { lang } = useOutletContext();
  const zh = lang === "zh";
  return (
    <div className="w-full max-w-3xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-bold text-white mb-2">{zh ? "隐私政策" : "Privacy Policy"}</h1>
      <p className="text-gray-500 text-sm mb-10">{zh ? "最后更新" : "Last updated"}: {LAST_UPDATED}</p>

      {zh ? (
        <>
          <p className="text-gray-400 text-sm mb-8 bg-dark-card border border-dark-border rounded-lg p-4">
            本中文版本仅供参考。如与英文版本有任何不一致，以英文版本为准。
          </p>
          <ZhSections />
        </>
      ) : (
        <EnSections />
      )}

      <div className="mt-12 text-center">
        <Link to="/" className="text-primary hover:text-primary-dark transition text-sm">
          {zh ? "← 返回 ShadowVault" : "← Back to ShadowVault"}
        </Link>
      </div>
    </div>
  );
}
